import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  FACILITY_OPTIONS,
  FACILITY_TO_API_MAP,
  MARITAL_STATUS_OPTIONS,
  REQUEST_TYPE,
  createAchcRequest,
  fetchAchcHospitals,
  getEmployeeDisplayName,
  getLoggedInEmpId,
  getLoggedInUser,
  fetchSpouseDetails,
  isAchcSubmitSuccess,
  mapMaritalStatus,
  normalizeDobToDMY,
} from "./authorityLetterHealthCheckup.service";

const CreateAuthorityLetter: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const user = getLoggedInUser();
  const details = user?.details || {};
  const employeeName = getEmployeeDisplayName(user);

  const [hospitalOptions, setHospitalOptions] = useState<{ id: string; label: string }[]>([]);
  const [hospitalOptionsLoading, setHospitalOptionsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const mappedMarital = mapMaritalStatus(details?.marital_status);
  const employeeDob = normalizeDobToDMY(details?.birthdate || details?.dob);

  const [formData, setFormData] = useState({
    activePhoneNumber: String(details?.phone || details?.mobile || user?.phone || "")
      .replace(/\D/g, "")
      .slice(0, 10),
    emailId: String(details?.email || user?.email || ""),
    facilityRequiredFor: "",
    maritalStatus: mappedMarital,
    fromHospital: "",
    employeeDob,
    spouseName: "",
    spouseDob: "",
  });

  const showSpouseFields =
    formData.facilityRequiredFor === "SPOUSE" || formData.facilityRequiredFor === "BOTH";
  const showEmployeeDob =
    formData.facilityRequiredFor === "SELF" || formData.facilityRequiredFor === "BOTH";

  const loadHospitals = useCallback(async () => {
    setHospitalOptionsLoading(true);
    try {
      const options = await fetchAchcHospitals();
      setHospitalOptions(options);
    } catch (err: any) {
      setHospitalOptions([]);
      toast.error(Api.getErrorMessage(err) || t("failed_to_load_details"));
    } finally {
      setHospitalOptionsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadHospitals();
  }, [loadHospitals]);

  useEffect(() => {
    let cancelled = false;
    fetchSpouseDetails()
      .then((spouse) => {
        if (cancelled) return;
        setFormData((prev) => ({
          ...prev,
          spouseName: spouse.spouseName || prev.spouseName,
          spouseDob: spouse.spouseDob || prev.spouseDob,
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const nextMarital = mapMaritalStatus(details?.marital_status);
    const nextDob = normalizeDobToDMY(details?.birthdate || details?.dob);
    setFormData((prev) => ({
      ...prev,
      maritalStatus: nextMarital || prev.maritalStatus,
      employeeDob: nextDob || prev.employeeDob,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details?.marital_status, details?.birthdate]);

  const handleInputChange = (key: string, value: any) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  const validateForm = (): boolean => {
    if (!formData.activePhoneNumber.trim()) {
      toast.error(t("please_enter_phone_number") || "Please enter phone number");
      return false;
    }
    if (!/^\d{10}$/.test(formData.activePhoneNumber.trim())) {
      toast.error(t("invalid_phone_number") || "Phone number must be 10 digits");
      return false;
    }
    if (!formData.emailId.trim()) {
      toast.error(t("please_enter_email") || "Please enter email ID");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId.trim())) {
      toast.error(t("invalid_email") || "Please enter a valid email address");
      return false;
    }
    if (!formData.facilityRequiredFor) {
      toast.error(t("please_select_facility") || "Please select facility required for");
      return false;
    }
    if (!formData.maritalStatus) {
      toast.error(t("please_select_marital_status") || "Please select marital status");
      return false;
    }
    if (!formData.fromHospital) {
      toast.error(t("please_select_hospital") || "Please select a hospital");
      return false;
    }
    if (showEmployeeDob && !formData.employeeDob) {
      toast.error(t("please_select_dob") || "Please select your date of birth");
      return false;
    }
    if (showSpouseFields) {
      if (!formData.spouseName.trim()) {
        toast.error(t("please_enter_spouse_name") || "Please enter spouse name");
        return false;
      }
      if (!formData.spouseDob) {
        toast.error(t("please_select_spouse_dob") || "Please select spouse date of birth");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    const empId = getLoggedInEmpId();
    if (!empId) {
      toast.error(t("employee_id_not_found"));
      return;
    }
    setSubmitting(true);
    try {
      const payload: any = {
        Action: "C",
        ReferenceNumber: "",
        EmpID: empId,
        RequestTypeForMedicalAttendanceRules: REQUEST_TYPE,
        HospitalRecordKey: formData.fromHospital,
        ComprehensiveCheckupFor: FACILITY_TO_API_MAP[formData.facilityRequiredFor] ?? "E",
        MaritalStatus: formData.maritalStatus,
        ContactNumber: formData.activePhoneNumber,
        EmailID: formData.emailId,
        ...(showEmployeeDob && { DateOfBirthOfEmployee: formData.employeeDob }),
      };
      const res: any = await createAchcRequest(payload);
      if (isAchcSubmitSuccess(res)) {
        setShowThankYou(true);
      } else {
        toast.error(
          res?.data?.message || res?.message || t("something_went_wrong") || "Failed to submit"
        );
      }
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || t("something_went_wrong") || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const required = <span className="text-danger">*</span>;
  const designation =
    details?.designation_details?.longText ||
    details?.sap_department_designation_wings?.designation_name ||
    "-";
  const department = details?.sap_department_designation_wings?.department_name || "-";
  const maritalLabel =
    MARITAL_STATUS_OPTIONS.find((o) => o.id === formData.maritalStatus)?.label || "";

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              {t("authority_letter_for_annual_comprehensive_health_checkup")}
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.authorityLetterHealthCheckup.list}>
                    {t("authority_letter_for_annual_comprehensive_health_checkup")}
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("create_new_request")}
                </li>
              </ol>
            </nav>
          </div>
          <Link to={routes.authorityLetterHealthCheckup.list} className="btn btn-light">
            <i className="ti ti-arrow-left me-1"></i>
            {t("close")}
          </Link>
        </div>

        <div className="alert alert-light border d-flex align-items-center mb-3">
          <i className="ti ti-heartbeat text-danger me-2 fs-20"></i>
          <div>
            <div className="fw-bold">{t("annual_comprehensive")}</div>
            <div className="small text-muted">{t("fill_all_required")}</div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Section title={t("employee_information")}>
            <div className="row g-3">
              <ReadOnlyField label={t("name")} value={employeeName} />
              <ReadOnlyField label={t("designation")} value={designation} />
              <ReadOnlyField label={t("payscale")} value={details?.pay_scale || "-"} />
              <ReadOnlyField
                label={t("mode_of_recruitment")}
                value={user?.employmentType || details?.employmentType || "-"}
              />
              <ReadOnlyField
                label={t("employment_status")}
                value={details?.empStatus || user?.empStatus || "-"}
              />
              <ReadOnlyField label={t("department")} value={department} />
            </div>
          </Section>

          <Section title={t("contact_details") || "Contact Details"}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {t("active_phone_number")} {required}
                </label>
                <input
                  type="tel"
                  className="form-control"
                  maxLength={10}
                  value={formData.activePhoneNumber}
                  onChange={(e) =>
                    handleInputChange("activePhoneNumber", e.target.value.replace(/\D/g, ""))
                  }
                  placeholder={t("enter_phone_number")}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {t("email_id")} {required}
                </label>
                <input
                  type="email"
                  className="form-control"
                  value={formData.emailId}
                  onChange={(e) => handleInputChange("emailId", e.target.value)}
                  placeholder={t("enter_email")}
                />
              </div>
            </div>
          </Section>

          <Section title={t("application_for")}>
            <div className="form-control bg-light">
              {t("annual_comprehensive_medical")}
            </div>
          </Section>

          <Section title={t("health_check_details")}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {t("annual_comprehensive_facility_required_for")} {required}
                </label>
                <select
                  className="form-select"
                  value={formData.facilityRequiredFor}
                  onChange={(e) => handleInputChange("facilityRequiredFor", e.target.value)}
                >
                  <option value="">{t("select_facility")}</option>
                  {FACILITY_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {t("marital_status")} {required}
                </label>
                <input className="form-control" value={maritalLabel} disabled />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {t("from_hospital")} {required}
                </label>
                <select
                  className="form-select"
                  value={formData.fromHospital}
                  onChange={(e) => handleInputChange("fromHospital", e.target.value)}
                  disabled={hospitalOptionsLoading}
                >
                  <option value="">
                    {hospitalOptionsLoading ? t("loading") || "Loading..." : t("select_hospital")}
                  </option>
                  {hospitalOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              {showEmployeeDob && (
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {t("dob_employee")} {required}
                  </label>
                  <input className="form-control" value={formData.employeeDob} disabled />
                </div>
              )}
            </div>

            {showSpouseFields && (
              <div className="mt-4 pt-3 border-top">
                <span className="badge bg-warning text-dark mb-3">{t("spouse_details")}</span>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      {t("spouse_name")} {required}
                    </label>
                    <input className="form-control" value={formData.spouseName} disabled />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">
                      {t("spouse_dob")} {required}
                    </label>
                    <input className="form-control" value={formData.spouseDob} disabled />
                  </div>
                </div>
              </div>
            )}
          </Section>

          <div className="d-flex justify-content-end gap-2 mb-4">
            <Link to={routes.authorityLetterHealthCheckup.list} className="btn btn-light">
              {t("cancel")}
            </Link>
            <button type="submit" className="btn btn-danger" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" />
                  {t("submit")}
                </>
              ) : (
                t("submit")
              )}
            </button>
          </div>
        </form>

        {showThankYou && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center py-4">
                  <i className="ti ti-circle-check text-success fs-1 d-block mb-2"></i>
                  <h5>
                    {t("thank_you_application_submitted_successfully") ||
                      "Thank you. Application submitted successfully."}
                  </h5>
                  <button
                    className="btn btn-danger mt-3"
                    onClick={() => navigate(routes.authorityLetterHealthCheckup.list)}
                  >
                    {t("ok") || "OK"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="card border-0 shadow-sm mb-3">
    <div className="card-header bg-white">
      <h5 className="mb-0 fw-bold">{title}</h5>
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const ReadOnlyField: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="col-md-6">
    <label className="form-label fw-semibold">{label}</label>
    <input className="form-control" value={value ?? "-"} disabled />
  </div>
);

export default CreateAuthorityLetter;
